import { useState, useEffect, useCallback, useRef } from 'react';
import { useVersionedApi } from '../utils/versionedApi.js';

import { safeStorage } from '../utils/safeStorage';
import { useAuth } from '../AuthContext';
/**
 * Unified notifications hook — reads from persisted Notification table.
 * Replaces useAlerts for the bell icon and notifications page.
 *
 * Features:
 * - Paginated list with category filter
 * - Unread count for badge
 * - Mark-as-read (single + all)
 * - SSE for real-time updates
 * - Auto-poll fallback every 2 minutes, only while the stream is down
 */

/** Reconnect backoff: 30s, 1m, 2m, 4m, then capped. */
const RECONNECT_BASE_MS = 30 * 1000;
const RECONNECT_MAX_MS = 5 * 60 * 1000;
/** Spread simultaneous reconnects so a restart is not met by one synchronised wave. */
const RECONNECT_JITTER_MS = 10 * 1000;
/** How often to ask for the unread count while the stream is unavailable. */
const FALLBACK_POLL_MS = 2 * 60 * 1000;
export const useNotifications = () => {
  const { user, loading: authLoading, authChecked, can } = useAuth();
  // Notifications are tenant-scoped and permission-gated. Do not begin polling
  // while auth is restoring a cached session, for a user without an organization
  // context, or for a role the server would reject with view_notifications.
  // In particular, this prevents a stale token from issuing retried requests on
  // the login screen before /users/me has established an authenticated user.
  const hasNotificationAccess = Boolean(
    user?.id
    && user?.organizationId
    && can?.('view_notifications')
  );
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const apiClient = useVersionedApi('v1');
  const sseRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastFetchedRef = useRef(null);
  // The poll below is a FALLBACK, not a second delivery path: it only issues a
  // request while the stream is down. Read through a ref rather than state so
  // flipping it never re-runs the polling effect (which would restart its
  // interval and defeat the gate).
  const sseConnectedRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // ── Fetch notifications list ──
  const fetchNotifications = useCallback(async ({ page = 1, limit = 20, category = 'all', unreadOnly = false } = {}) => {
    const token = safeStorage.getItem('evv_token');
    if (authLoading || authChecked === false || !hasNotificationAccess || !token) return;

    try {
      setLoading(true);
      setError(null);

      const params = { page, limit };
      if (category && category !== 'all') params.category = category;
      if (unreadOnly) params.unreadOnly = 'true';

      const response = await apiClient.get('/notifications', { params });
      const data = response.data;

      setNotifications(data.notifications || []);
      setPagination(data.pagination || { page, pageSize: limit, total: 0, totalPages: 0 });
      lastFetchedRef.current = Date.now();
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        // Silently skip permission/auth errors
        return;
      }
      console.error('Failed to fetch notifications:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [apiClient, authLoading, authChecked, hasNotificationAccess]);

  // ── Fetch unread count ──
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = safeStorage.getItem('evv_token');
      if (authLoading || authChecked === false || !hasNotificationAccess || !token) return;

      // The badge is non-critical. Fail quickly and let the next scheduled poll
      // retry instead of holding three 30-second requests during API pressure.
      const response = await apiClient.get('/notifications/unread-count', {
        timeout: 5000,
        retry: false,
      });
      setUnreadCount(response.data.unreadCount || 0);
    } catch (err) {
      // Silently fail — badge count is non-critical
      if (import.meta.env.DEV
        && err.response?.status !== 403
        && err.response?.status !== 401) {
        console.error('Failed to fetch unread count:', err);
      }
    }
  }, [apiClient, authLoading, authChecked, hasNotificationAccess]);

  // ── Mark single notification as read ──
  const markAsRead = useCallback(async (id) => {
    try {
      await apiClient.put(`/notifications/${id}/read`);

      // Optimistic update
      setNotifications(prev => prev.map(n =>
        n.id === id ? { ...n, readAt: new Date().toISOString(), status: 'read' } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, [apiClient]);

  // ── Mark all as read ──
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await apiClient.put('/notifications/mark-all-read');

      // Update local state
      setNotifications(prev => prev.map(n => ({
        ...n,
        readAt: n.readAt || new Date().toISOString(),
        status: 'read'
      })));
      setUnreadCount(0);

      return response.data;
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      throw err;
    }
  }, [apiClient]);

  // ── Refresh ──
  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  // ── SSE connection for real-time updates ──
  useEffect(() => {
    const token = safeStorage.getItem('evv_token');
    if (authLoading || authChecked === false || !hasNotificationAccess || !token) return;

    const connectSSE = () => {
      try {
        // Import API_BASE_URL dynamically to avoid circular deps at module level
        const baseUrl = safeStorage.getItem('evv_api_base') || '';
        if (!baseUrl) return;

        const url = `${baseUrl}/notifications/stream`;
        const eventSource = new EventSource(url, { withCredentials: true });

        eventSource.onopen = () => {
          sseConnectedRef.current = true;
          reconnectAttemptsRef.current = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'notification') {
              // New notification arrived — bump count and prepend
              setUnreadCount(prev => prev + 1);
              setNotifications(prev => {
                const newNotification = {
                  id: payload.data.id,
                  templateId: payload.data.templateId || '',
                  title: payload.data.title,
                  content: payload.data.content,
                  priority: payload.data.priority,
                  status: 'delivered',
                  readAt: null,
                  createdAt: payload.data.timestamp || new Date().toISOString(),
                  data: payload.data.data || null
                };
                return [newNotification, ...prev].slice(0, 50);
              });
            }
          } catch (e) {
            // Ignore parse errors from pings
          }
        };

        eventSource.onerror = () => {
          eventSource.close();
          // The stream is down, so the fallback poll takes over until it is back.
          sseConnectedRef.current = false;
          // Back off instead of retrying on a fixed 30s beat: a backend restart
          // drops every open tab at once, and a fixed delay marches them all
          // back in step. Capped at 5 minutes, with jitter to spread the herd.
          const attempt = Math.min(reconnectAttemptsRef.current, 4);
          reconnectAttemptsRef.current = attempt + 1;
          const backoff = Math.min(RECONNECT_BASE_MS * (2 ** attempt), RECONNECT_MAX_MS);
          const delay = backoff + Math.floor(Math.random() * RECONNECT_JITTER_MS);
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(connectSSE, delay);
        };

        sseRef.current = eventSource;
      } catch (e) {
        // SSE not supported or connection failed — the poll below carries it.
        sseConnectedRef.current = false;
      }
    };

    connectSSE();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      reconnectAttemptsRef.current = 0;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      // Teardown is not a live stream, so the poll must not treat it as one.
      sseConnectedRef.current = false;
    };
  }, [authLoading, authChecked, hasNotificationAccess]);

  // ── Fallback poll: runs only while the SSE stream is down ──
  useEffect(() => {
    const token = safeStorage.getItem('evv_token');
    if (authLoading || authChecked === false || !hasNotificationAccess || !token) return;

    // Initial fetch
    fetchUnreadCount();

    pollIntervalRef.current = setInterval(() => {
      // While the stream is connected it already delivers every new
      // notification, so a request here would fetch what we have been told.
      if (sseConnectedRef.current) return;
      const currentToken = safeStorage.getItem('evv_token');
      if (currentToken && hasNotificationAccess) {
        fetchUnreadCount();
      }
    }, FALLBACK_POLL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [authLoading, authChecked, fetchUnreadCount, hasNotificationAccess]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    refreshNotifications
  };
};
